// lib/supabaseClient.js
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Create a mock client if vars are missing to prevent app crash
const isConfigured = supabaseUrl && supabaseAnonKey && supabaseUrl !== 'https://your-project.supabase.co';

if (!isConfigured) {
  console.warn('Supabase environment variables missing or default. Usage will result in errors.');
}

export const supabase = isConfigured
  ? createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    }
  })
  : {
    auth: {
      signUp: async () => ({ error: { message: 'Supabase not configured' } }),
      signInWithPassword: async () => ({ error: { message: 'Supabase not configured' } }),
      signOut: async () => ({ error: null }),
      getUser: async () => ({ data: { user: null }, error: null }),
      getSession: async () => ({ data: { session: null }, error: null }),
      onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => { } } } })
    },
    from: () => ({
      select: () => ({
        eq: () => ({
          single: async () => ({ error: { message: 'Supabase not configured' } }),
          order: () => ({ limit: async () => ({ error: { message: 'Supabase not configured' } }) })
        }),
        order: () => ({ limit: async () => ({ error: { message: 'Supabase not configured' } }) }),
        limit: async () => ({ error: { message: 'Supabase not configured' } })
      }),
      insert: () => ({ select: () => ({ single: async () => ({ error: { message: 'Supabase not configured' } }) }) }),
      update: () => ({ eq: () => ({ select: () => ({ single: async () => ({ error: { message: 'Supabase not configured' } }) }) }) }),
      delete: () => ({ eq: async () => ({ error: { message: 'Supabase not configured' } }) })
    }),
    storage: {
      from: () => ({
        upload: async () => ({ error: { message: 'Supabase not configured' } }),
        getPublicUrl: () => ({ data: { publicUrl: '' } }),
        remove: async () => ({ error: { message: 'Supabase not configured' } })
      })
    }
  };

// Auth helpers
export const signUp = async (email, password, userData) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: userData
    }
  });
  return { data, error };
};

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  return { data, error };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  return { user, error };
};

export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  return { session, error };
};

// Storage helpers
export const uploadFile = async (bucket, path, file) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false
    });

  if (error) return { data: null, error };

  const { data: urlData } = supabase.storage
    .from(bucket)
    .getPublicUrl(path);

  return { data: { ...data, publicUrl: urlData.publicUrl }, error: null };
};

export const deleteFile = async (bucket, path) => {
  const { error } = await supabase.storage
    .from(bucket)
    .remove([path]);
  return { error };
};

// Database helpers
export const insertRecord = async (table, record) => {
  const { data, error } = await supabase
    .from(table)
    .insert([record])
    .select()
    .single();
  return { data, error };
};

export const updateRecord = async (table, id, updates) => {
  const { data, error } = await supabase
    .from(table)
    .update(updates)
    .eq('id', id)
    .select()
    .single();
  return { data, error };
};

export const deleteRecord = async (table, id) => {
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('id', id);
  return { error };
};

export const getRecords = async (table, filters = {}, options = {}) => {
  let query = supabase.from(table).select(options.select || '*');

  // Apply filters
  Object.entries(filters).forEach(([key, value]) => {
    query = query.eq(key, value);
  });

  // Apply sorting
  if (options.orderBy) {
    query = query.order(options.orderBy, {
      ascending: options.ascending !== false
    });
  }

  // Apply limit
  if (options.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;
  return { data, error };
};

export default supabase;